import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, Sparkles, AlertTriangle, CircleCheck, Loader2, FileText, RefreshCcw, Download, FilePenLine,
} from "lucide-react";

/**
 * ALTIORA V3 · Sécurité Financière — module dashboard embedded in DossierDetail.
 *
 * Props:
 *   dossierId  — string
 *   dossier    — current dossier snapshot (for economic_data hints)
 */
const SEVERITY_STYLE = {
  urgent:    { bg: "bg-[#FEE2E2]", fg: "text-[#7F1D1D]", label: "URGENT", icon: AlertTriangle },
  attention: { bg: "bg-[#FBF6EA]", fg: "text-[#7A5B14]", label: "ATTENTION", icon: AlertTriangle },
  ok:        { bg: "bg-[#D1FAE5]", fg: "text-[#065F46]", label: "CONFORME", icon: CircleCheck },
};

const CATEGORY_LABEL = {
  assurance_vie: "Assurance-vie",
  assurance_invalidite: "Assurance-invalidité",
  assurance_maladies_graves: "Assurance maladies graves",
  assurance_soins_ld: "Soins longue durée",
  epargne_retraite: "Épargne — retraite",
  epargne_projet: "Épargne — projet",
  placements: "Placements",
  credit: "Crédit",
  hypotheque: "Hypothèque",
  affaires_associes: "Convention & rachat entre actionnaires",
  affaires_homme_cle: "Homme-clé",
  transmission: "Transmission",
  fonds_urgence: "Fonds d'urgence",
};

export default function ModuleSecuriteFinanciere({ dossierId, dossier }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [jobStatus, setJobStatus] = useState(null); // {status, progress, error}
  const [downloading, setDownloading] = useState("");

  const loadCached = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/modules/securite_financiere/analysis/${dossierId}`);
      setAnalysis(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setAnalysis(null);
      } else {
        toast.error("Chargement de l'analyse impossible");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { loadCached(); }, [dossierId]);

  const runAnalysis = async () => {
    setRunning(true);
    setJobStatus({ status: "queued", progress: 0 });
    try {
      const { data } = await api.post(`/modules/securite_financiere/analyze/${dossierId}`);
      const jobId = data?.job?.job_id;
      if (!jobId) throw new Error("no job_id");

      // Poll until done (up to 5 minutes)
      const started = Date.now();
      while (Date.now() - started < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: job } = await api.get(`/modules/securite_financiere/jobs/${jobId}`);
        setJobStatus({ status: job.status, progress: job.progress, error: job.error });
        if (job.status === "done") {
          setAnalysis(job.result);
          toast.success("Analyse mise à jour");
          return;
        }
        if (job.status === "failed" || job.status === "timeout") {
          toast.error(job.error || "Analyse impossible", { duration: 10000 });
          await loadCached();
          return;
        }
      }
      toast.error("Analyse trop longue — le job continue en arrière-plan.", { duration: 8000 });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Analyse impossible", { duration: 8000 });
    } finally {
      setRunning(false);
      setJobStatus(null);
    }
  };

  const downloadReport = async (variant) => {
    setDownloading(variant);
    try {
      const backendBase = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("altiora_token");
      const url = `${backendBase}/api/modules/securite_financiere/report/${dossierId}?variant=${variant}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `altiora-securite-financiere-${variant === "draft" ? "brouillon-signature" : "informationnel"}-${dossierId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (err) {
      toast.error("Téléchargement du PDF impossible");
    } finally { setDownloading(""); }
  };

  const det = analysis?.deterministic || null;
  const ai = analysis?.ai || null;
  const findings = det?.findings || [];
  const kpis = det?.kpis || {};
  const warnings = det?.warnings || [];
  const recs = ai?.recommendations || [];

  // Group by category for the tab-like layout
  const groupedRecs = useMemo(() => {
    const g = {};
    recs.forEach((r) => {
      const c = r.category || "autre";
      if (!g[c]) g[c] = [];
      g[c].push(r);
    });
    return g;
  }, [recs]);

  if (loading) {
    return (
      <div className="rounded-xl border border-[#E4E1D8] bg-white p-6 text-sm text-[#5B5B62]">Chargement de l&apos;analyse…</div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sf-module-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
            Module ALTIORA
          </div>
          <h2 className="mt-1 font-display text-2xl tracking-tight text-[#1F2937] flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#0B2545]" strokeWidth={1.5} />
            Sécurité financière intégrée
          </h2>
          <p className="mt-1 text-sm text-[#5B5B62] max-w-2xl leading-relaxed">
            Analyse de vos besoins et couvertures : assurance-vie, invalidité, maladies graves,
            fonds d&apos;urgence, épargne, placements, crédit &amp; hypothèque.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={runAnalysis}
            disabled={running}
            data-testid="sf-run-analysis"
            className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-4 py-2 text-sm transition-colors"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" strokeWidth={1.8} />}
            {running ? "Analyse en cours…" : (analysis ? "Recalculer" : "Lancer l'analyse")}
          </button>
          {analysis && (
            <>
              <button
                onClick={() => downloadReport("info")}
                disabled={!!downloading}
                data-testid="sf-download-info"
                className="inline-flex items-center gap-2 rounded-md border border-[#E4E1D8] hover:border-[#0B2545] bg-white px-4 py-2 text-sm transition-colors"
              >
                {downloading === "info" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" strokeWidth={1.8} />}
                Rapport informationnel
              </button>
              <button
                onClick={() => downloadReport("draft")}
                disabled={!!downloading}
                data-testid="sf-download-draft"
                className="inline-flex items-center gap-2 rounded-md border border-[#7A5B14] text-[#7A5B14] hover:bg-[#FBF6EA] bg-white px-4 py-2 text-sm transition-colors"
              >
                {downloading === "draft" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePenLine className="h-4 w-4" strokeWidth={1.8} />}
                Brouillon signature CSF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Async job progress banner */}
      {running && (
        <div className="mb-6 rounded-xl border border-[#0B2545]/20 bg-white p-5 flex items-start gap-4" data-testid="sf-job-banner">
          <Loader2 className="h-5 w-5 animate-spin text-[#0B2545] mt-0.5" strokeWidth={1.5} />
          <div className="flex-1">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#0B2545]">
              {jobStatus?.status === "queued" ? "En file d'attente" : "Analyse en cours…"}
            </div>
            <div className="mt-1 text-sm text-[#1F2937]">
              La narration IA du CSF peut prendre 60 à 90 secondes. Vous pouvez rester ici — nous vous préviendrons dès qu&apos;elle est prête.
            </div>
            <div className="mt-3 h-1 w-full bg-[#F1EFE8] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#0B2545] to-[#7A5B14] animate-pulse" style={{ width: "100%" }} />
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!analysis && (
        <div className="rounded-xl border border-dashed border-[#E4E1D8] bg-white p-10 text-center">
          <Sparkles className="h-8 w-8 text-[#B8B5AC] mx-auto" strokeWidth={1.5} />
          <div className="mt-4 font-display text-lg text-[#1F2937]">Aucune analyse encore effectuée</div>
          <div className="mt-2 text-sm text-[#5B5B62] max-w-md mx-auto leading-relaxed">
            Complétez le questionnaire de votre dossier puis cliquez sur <b>Lancer l&apos;analyse</b> ci-dessus
            pour obtenir vos constats déterministes et vos recommandations personnalisées.
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="rounded-lg border border-[#C5A880]/40 bg-[#FBF6EA] p-4" data-testid="sf-warnings">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] mb-2">
            Limites d&apos;analyse
          </div>
          <ul className="space-y-1">
            {warnings.map((w, i) => (
              <li key={i} className="text-xs text-[#5B5B62] leading-relaxed flex items-start gap-2">
                <span className="mt-1 h-1 w-1 rounded-full bg-[#7A5B14] flex-shrink-0" /><span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* KPIs quick tiles */}
      {analysis && Object.keys(kpis).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="sf-kpis">
          <KpiTile label="Assurance-vie — écart" value={kpis.life_gap} highlight />
          <KpiTile label="Invalidité — besoin mensuel" value={kpis.disability_need_monthly} suffix="/mois" />
          <KpiTile label="Maladies graves — cible" value={kpis.critical_illness_target} />
        </div>
      )}

      {/* Deterministic findings */}
      {analysis && findings.length > 0 && (
        <div data-testid="sf-findings">
          <h3 className="font-display text-lg text-[#1F2937] tracking-tight mb-3">Constats déterministes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {findings.map((f, i) => (
              <FindingCard key={i} finding={f} />
            ))}
          </div>
        </div>
      )}

      {/* AI recommendations */}
      {analysis && (
        <div data-testid="sf-ai-block">
          <h3 className="font-display text-lg text-[#1F2937] tracking-tight mb-3">
            Recommandations personnalisées
          </h3>
          {!ai?.available && (
            <div className="rounded-lg border border-[#E4E1D8] bg-white p-4 text-sm text-[#5B5B62]">
              {ai?.error || "Le moteur IA est momentanément indisponible."}{" "}
              Les constats déterministes ci-dessus sont pleinement utilisables. Cliquez sur
              <b> Recalculer</b> pour retenter la génération narrative.
            </div>
          )}
          {ai?.available && ai.executive_summary_fr && (
            <div className="rounded-xl border border-[#0B2545]/20 bg-white p-5 mb-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] mb-1">Synthèse</div>
              <p className="text-[15px] text-[#1F2937] leading-relaxed">{ai.executive_summary_fr}</p>
              {ai.assumptions_fr?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#E4E1D8]">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-1">Hypothèses posées</div>
                  <ul className="space-y-0.5">
                    {ai.assumptions_fr.map((a, i) => <li key={i} className="text-xs text-[#5B5B62]">· {a}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          {ai?.available && recs.length > 0 && (
            <div className="space-y-4">
              {Object.entries(groupedRecs).map(([cat, list]) => (
                <div key={cat} className="rounded-xl border border-[#E4E1D8] bg-white p-5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] mb-3">
                    {CATEGORY_LABEL[cat] || cat}
                  </div>
                  <div className="space-y-4">
                    {list.map((r, i) => <RecoBlock key={i} reco={r} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {analysis && (
        <div className="text-[10px] font-mono text-[#8B8B8F] text-right">
          Dernière analyse : {new Date(analysis.computed_at).toLocaleString("fr-CA")} · Méthode {analysis.method_version}
        </div>
      )}
    </div>
  );
}

/* -------------------- Sub-components -------------------- */

function KpiTile({ label, value, suffix = "", highlight = false }) {
  const fmt = (v) => {
    if (v == null || v === 0) return "—";
    try { return Number(v).toLocaleString("fr-CA", { maximumFractionDigits: 0 }) + " $"; }
    catch { return "—"; }
  };
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-[#7A5B14]/40 bg-[#FBF6EA]" : "border-[#E4E1D8] bg-white"}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">{label}</div>
      <div className={`mt-1 font-display text-2xl tracking-tight ${highlight ? "text-[#7A5B14]" : "text-[#0B2545]"}`}>
        {fmt(value)}{suffix && value ? <span className="text-xs text-[#8B8B8F] ml-1">{suffix}</span> : null}
      </div>
    </div>
  );
}

function FindingCard({ finding }) {
  const s = SEVERITY_STYLE[finding.severity] || SEVERITY_STYLE.ok;
  const Icon = s.icon;
  return (
    <div className="rounded-xl border border-[#E4E1D8] bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 rounded-full ${s.bg} ${s.fg} px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest`}>
          <Icon className="h-2.5 w-2.5" strokeWidth={2} /> {s.label}
        </span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
          {CATEGORY_LABEL[finding.category] || finding.category}
        </span>
      </div>
      <div className="mt-2 font-display text-base text-[#1F2937] tracking-tight">{finding.title_fr}</div>
      <p className="mt-1.5 text-[13px] text-[#5B5B62] leading-relaxed">{finding.detail_fr}</p>
      {finding.recommended_action_fr && (
        <p className="mt-2 text-xs text-[#374151] leading-relaxed">
          <span className="font-semibold">Action suggérée : </span>{finding.recommended_action_fr}
        </p>
      )}
    </div>
  );
}

function RecoBlock({ reco }) {
  const prio = (reco.priority || "").toUpperCase();
  const prioColor = prio === "URGENT" ? "text-[#7F1D1D]" : prio === "COURT_TERME" ? "text-[#7A5B14]" : "text-[#0B2545]";
  return (
    <div className="border-l-2 border-[#0B2545] pl-4 py-1">
      <div className={`text-[10px] font-mono uppercase tracking-widest ${prioColor}`}>
        {prio.replace(/_/g, " ")}
      </div>
      <div className="mt-1 font-display text-base text-[#1F2937] tracking-tight">{reco.title_fr}</div>
      {reco.rationale_fr && (
        <p className="mt-1.5 text-[13px] text-[#5B5B62] leading-relaxed">{reco.rationale_fr}</p>
      )}
      {reco.actions_fr?.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {reco.actions_fr.map((a, i) => (
            <li key={i} className="text-[13px] text-[#374151] leading-relaxed flex items-start gap-2">
              <span className="mt-1.5 h-1 w-1 rounded-full bg-[#0B2545] flex-shrink-0" /><span>{a}</span>
            </li>
          ))}
        </ul>
      )}
      {reco.products_hint_fr?.length > 0 && (
        <div className="mt-2 text-xs text-[#5B5B62]">
          <span className="font-semibold">Produits à explorer : </span>{reco.products_hint_fr.join(", ")}
        </div>
      )}
    </div>
  );
}
